import type { ConversationView } from '@swchat/client-sdk';
import type { Project } from '@swchat/protocol';
import { Avatar, Badge, Button, ContextPanel, ContextSection, Icon } from '../design';
import { portalProjectUrl } from '../lib/client';
import { ROLE_LABEL } from '../lib/group';
import { useClient } from '../lib/hooks';

/**
 * Khung ngữ cảnh của hội thoại đang mở: giới thiệu, dự án gắn với nhóm (mã, tên, trạng thái — từ dự án đã đồng bộ E1;
 * liên kết "Mở trên 7wportal" — W2.5), thành viên, mã an toàn (1-1). Không có dữ liệu portal khác (tiến độ %, công việc,
 * phê duyệt… — W3).
 */
export function ConversationContext({
  conversation,
  project,
  safety,
  drawer = false,
  onVerify,
  onManage,
  onClose,
  onOpenExternal,
}: {
  conversation: ConversationView;
  project?: Project;
  safety?: string;
  drawer?: boolean;
  onVerify?: () => void;
  onManage?: () => void;
  onClose: () => void;
  /** Trong `/embed`: nhờ portal mở URL (thông điệp `open_external`) thay vì mở tab mới từ iframe. */
  onOpenExternal?: (url: string) => void;
}) {
  const client = useClient();
  const direct = conversation.kind === 'direct';
  const other = direct ? conversation.members.find((m) => m.user_id !== client.userId) : undefined;
  const portalUrl = project && portalProjectUrl(project.id);
  return (
    <ContextPanel
      title={direct ? 'Thông tin cuộc trò chuyện' : 'Thông tin nhóm'}
      onClose={onClose}
      testId="context-panel"
      drawer={drawer}
      footer={
        <>
          <Icon name="lock" size={12} />
          Nội dung chỉ giải mã trên thiết bị của thành viên
        </>
      }
    >
      <ContextSection title="Giới thiệu" testId="context-intro">
        <div className="ws-context-intro">
          <Avatar name={conversation.title} seed={conversation.id} size="lg" />
          <div>
            <strong>{conversation.title}</strong>
            <div className="ws-muted ws-small">
              {direct ? 'Chat 1-1' : `Nhóm · ${conversation.members.length} thành viên`}
              {conversation.secret && ' · bí mật'}
            </div>
          </div>
        </div>
      </ContextSection>

      <ContextSection title="ID nhóm" testId="context-chat-id">
        <p className="ws-muted ws-small">Dùng làm <code>chat_id</code> khi bot gửi tin vào {direct ? 'cuộc trò chuyện' : 'nhóm'} này.</p>
        <div className="ws-row">
          <code className="ws-mono ws-chat-id ws-grow" data-testid="chat-id">
            {conversation.id}
          </code>
          <Button
            size="sm"
            data-testid="chat-id-copy"
            onClick={() => void navigator.clipboard?.writeText(conversation.id).catch(() => undefined)}
          >
            Sao chép
          </Button>
        </div>
      </ContextSection>

      {conversation.project_id && (
        <ContextSection title="Dự án" testId="context-project">
          {project ? (
            <div className="ws-context-project">
              <Badge tone="accent">#{project.code}</Badge>
              <strong>{project.name}</strong>
              <Badge tone="neutral">{project.status}</Badge>
              {portalUrl && (
                <a
                  className="ws-btn ws-btn-outline ws-btn-sm ws-context-portal"
                  data-testid="open-portal-project"
                  href={portalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={
                    onOpenExternal &&
                    ((e) => {
                      e.preventDefault();
                      onOpenExternal(portalUrl);
                    })
                  }
                >
                  <Icon name="external" size={14} />
                  Mở trên 7wportal
                </a>
              )}
            </div>
          ) : (
            <p className="ws-muted ws-small">Dự án chưa đồng bộ trên thiết bị này.</p>
          )}
        </ContextSection>
      )}

      {!direct && (
        <ContextSection
          title={`Thành viên (${conversation.members.length})`}
          action={
            onManage && (
              <Button size="sm" variant="ghost" onClick={onManage}>
                Quản lý
              </Button>
            )
          }
          testId="context-members"
        >
          <ul className="ws-member-mini">
            {conversation.members.map((m) => {
              const name = client.getUser(m.user_id)?.display_name ?? '…';
              return (
                <li key={m.user_id}>
                  <Avatar name={name} seed={m.user_id} size="sm" />
                  <span className="ws-member-mini-name">
                    {name}
                    {m.user_id === client.userId && <span className="ws-muted"> (bạn)</span>}
                  </span>
                  {m.role !== 'member' && <Badge tone="neutral">{ROLE_LABEL[m.role]}</Badge>}
                </li>
              );
            })}
          </ul>
        </ContextSection>
      )}

      {other && (
        <ContextSection title="Mã an toàn" testId="context-safety">
          {safety ? (
            <div className="ws-safety" data-testid="safety-number">
              <p className="ws-small">
                So sánh dãy số này với {conversation.title} (gặp trực tiếp hoặc gọi điện). Trùng khớp nghĩa là không có ai đứng giữa.
              </p>
              <code className="ws-mono">{safety}</code>
            </div>
          ) : (
            <>
              <p className="ws-muted ws-small">Đối chiếu mã an toàn để chắc chắn không có ai đứng giữa cuộc trò chuyện.</p>
              {onVerify && (
                <Button size="sm" icon="shield" onClick={onVerify}>
                  Hiện mã an toàn
                </Button>
              )}
            </>
          )}
        </ContextSection>
      )}
    </ContextPanel>
  );
}
